import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import {
  getFeeChangeNoticeStatus,
  hideFeeChangeNotice,
} from '@repository/feeChangeNotice';
import { useEffect, useRef, useState } from 'react';
import { FeeChangeModal } from './FeeChangeModal';

/**
 * O banner rola para fora junto com o conteúdo, então quem é fixo precisa
 * saber o quanto descer: a altura visível encolhe com o scroll (appbar,
 * gavetas) e a total, não (máscara do tour, min-height do conteúdo).
 */
const BANNER_HEIGHT_CSS_VAR = '--fee-banner-height';
const BANNER_VISIBLE_HEIGHT_CSS_VAR = '--fee-banner-visible-height';

export const FeeChangeBanner = () => {
  /**
   * Ler o localStorage já no primeiro render evita que o conteúdo "salte", e só
   * é seguro porque o Layout nunca é renderizado no servidor: o
   * PrivatePageWrapper (pages/_app.tsx) espera a sessão autenticar. Se isso
   * mudar, mova a leitura para um useEffect para não quebrar a hidratação.
   */
  const [show, setShow] = useState(getFeeChangeNoticeStatus);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    const setVar = (name: string, value: string) =>
      root.style.setProperty(name, value);

    const clear = () => {
      setVar(BANNER_HEIGHT_CSS_VAR, '0px');
      setVar(BANNER_VISIBLE_HEIGHT_CSS_VAR, '0px');
    };

    const banner = bannerRef.current;
    if (!banner) {
      clear();
      return;
    }

    let height = 0;
    let visible = -1;
    let frame: number | null = null;

    const publishVisible = () => {
      // O `min` segura o overscroll do iOS, que deixa scrollY negativo. Depois
      // que o banner sai da tela o valor para de mudar; reescrever a var
      // forçaria recálculo de estilo a cada frame à toa.
      const next = Math.min(height, Math.max(0, height - window.scrollY));
      if (next === visible) return;

      visible = next;
      setVar(BANNER_VISIBLE_HEIGHT_CSS_VAR, `${visible}px`);
    };

    // Só o próprio callback do rAF pode dar baixa no `frame`: se `measure`
    // fizesse isso ao publicar de forma síncrona, um frame pendente escaparia
    // do cleanup e reescreveria a var depois do `clear()`.
    const onFrame = () => {
      frame = null;
      publishVisible();
    };

    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(onFrame);
    };

    const measure = () => {
      // offsetHeight arredonda para inteiro e deixaria um vão de subpixel entre
      // o banner e a appbar.
      height = banner.getBoundingClientRect().height;
      setVar(BANNER_HEIGHT_CSS_VAR, `${height}px`);
      publishVisible();
    };
    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(banner);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
      clear();
    };
  }, [show]);

  const openModal = () => setIsModalOpen(true);

  const hideBanner = () => {
    hideFeeChangeNotice();
    setShow(false);
  };

  if (!show) return null;

  return (
    <>
      {/* `relative z-appbar` não tira o banner do fluxo, só o mantém à frente da
          gaveta de filtros, que é fixa e cobre a viewport inteira. */}
      <div
        ref={bannerRef}
        data-testid="fee-change-banner"
        className="divider relative z-appbar flex items-center gap-md bg-secondary/[0.08] px-main py-md"
      >
        <Typography className="grow text-sm">
          ⚠️{' '}
          <span className="font-semibold">
            As taxas da Vibx foram atualizadas!
          </span>{' '}
          {/* Um <button> não quebra junto com o texto: no mobile ele cairia
              inteiro para a linha seguinte. */}
          <span
            role="button"
            tabIndex={0}
            id="open-fee-change-modal"
            onClick={openModal}
            onKeyDown={(evt) => {
              if (evt.key !== 'Enter' && evt.key !== ' ') return;

              evt.preventDefault();
              openModal();
            }}
            className="cursor-pointer font-semibold text-primary underline transition hover:text-primaryHover"
          >
            Clique para saber mais
          </span>{' '}
          💰
        </Typography>

        <IconButton
          onClick={hideBanner}
          aria-label="Fechar aviso de mudança de taxas"
          className="shrink-0 p-0"
        >
          <CloseIcon />
        </IconButton>
      </div>

      <FeeChangeModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        onDontShowAgain={hideBanner}
      />
    </>
  );
};
